<template>
  <transition name="slide-up">
    <div v-if="isVisible" class="selection-bar">
      <div class="d-flex justify-content-between align-items-center">
        <!-- Left side: Selection count and clear -->
        <div class="d-flex align-items-center">
          <translate translate-context="edge-prints.selection"
                    :translate-n="totalSelected"
                    translate-plural="%{totalSelected} photos selected">
            %{totalSelected} photo selected
          </translate>
          <CButtonClose class="list-item-closer ml-2" @click="clearAllSelections">
            <CIcon name="cipClose" size="custom-size" width="15"/>
          </CButtonClose>
        </div>

        <!-- Right side: Actions -->
        <div class="d-flex align-items-center">
          <!-- Print selected (only if we have printable items selected) -->
          <CButton
            v-if="hasPrintableSelected"
            color="primary"
            size="sm"
            @click="printSelected"
            :disabled="printing || totalSelected === 0"
          >
            <CSpinner v-if="printing" size="sm" class="mr-1"/>
            <CIcon v-else name="cilPrint" class="mr-1"/>
            <translate translate-context="edge-prints.selection">Print Selected</translate>
          </CButton>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'SelectionBar',
  data() {
    return {
      printing: false
    }
  },
  computed: {
    ...mapGetters('edgePrints', [
      'getTotalSelectedCount',
      'getAllSelectedMedia',
      'getPrintJobsWithSelections'
    ]),

    totalSelected() {
      return this.getTotalSelectedCount
    },

    isVisible() {
      return this.totalSelected > 0
    },

    selectedMedia() {
      return this.getAllSelectedMedia
    },

    selectedPrintJobs() {
      return this.getPrintJobsWithSelections
    },

    hasPrintableSelected() {
      // Check if any selected media is from a printable order item OR from a print job
      const hasPrintableOrderItems = this.selectedMedia.some(item => item.orderItem?.is_printable === true)
      const hasPrintJobs = this.selectedPrintJobs.length > 0
      return hasPrintableOrderItems || hasPrintJobs
    }
  },
  methods: {
    ...mapActions('edgePrints', [
      'clearAllSelections'
    ]),

    printSelected() {
      if (this.totalSelected === 0) return

      const selectedMedia = this.selectedMedia
      const selectedPrintJobs = this.selectedPrintJobs

      // Group selected media by order item - ONLY PRINTABLE ITEMS
      const groupedByOrderItem = {}
      selectedMedia.forEach(item => {
        // Skip non-printable items
        if (!item.orderItem?.is_printable) return

        const key = item.orderItem.id
        if (!groupedByOrderItem[key]) {
          groupedByOrderItem[key] = {
            orderItem: item.orderItem,
            order: item.order,
            mediaIds: []
          }
        }
        groupedByOrderItem[key].mediaIds.push(item.mediaId)
      })

      const printableGroups = Object.values(groupedByOrderItem)

      // Add print job selections as separate groups
      selectedPrintJobs.forEach(({ printJob, selectedIds }) => {
        // For print jobs, we need to create a pseudo order item structure
        // Use the print job's configuration as the "product"
        const pseudoOrderItem = {
          id: `printjob_${printJob.id}`, // Special ID to identify it's from a print job
          is_printable: true,
          quantity: printJob.quantity || 1,
          product: {
            name: this.$pgettext('edge-prints.reprint', 'Reprint from Print Job'),
            config: printJob.print_config
          },
          product_variant: {
            config: printJob.print_config
          }
        }

        printableGroups.push({
          orderItem: pseudoOrderItem,
          order: this.$store.state.edgePrints.currentOrder || { picacode: printJob.picacode },
          mediaIds: selectedIds,
          isPrintJobReprint: true,
          originalPrintJob: printJob
        })
      })

      // Only proceed if we have printable items
      if (printableGroups.length > 0) {
        // Emit event to parent to open print summary modal
        this.$emit('open-print-summary', printableGroups)
      } else {
        this.notifyWarning(this.$pgettext('edge-prints.selection', 'No printable photos selected'))
      }
    }
  }
}
</script>

<style scoped>
.selection-bar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1050;
  padding: 12px 16px;
  min-width: 400px;
  max-width: 90%;
  border-radius: 12px;
  border: 1px solid #dee2e6;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0px 0.7px 1.4px 0px rgba(0, 0, 0, 0.07),
              0px 1.9px 4px 0px rgba(0, 0, 0, 0.05),
              0px 4.5px 10px 0px rgba(0, 0, 0, 0.05);
}

.list-item-closer {
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.list-item-closer:hover {
  opacity: 1;
}

/* Slide up animation */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  transform: translateX(-50%) translateY(100%);
  opacity: 0;
}
</style>